import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Tabs, Icon, ArticleCard, Skeleton, EmptyState, Button } from '../../components/ui';
import { fetchNews } from '../../services/newsService';

const FALLBACK_NEWS = [
  {
    title: 'Ram Mandir consecration ceremony marks historic milestone for Ayodhya',
    description: 'The Pran Pratishtha ceremony at Shri Ram Janmabhoomi Mandir was attended by the Prime Minister and thousands of devotees, marking a landmark event in the history of Ayodhya.',
    url: 'https://ayodhyadham.info',
    publishedAt: '2024-01-22T08:00:00Z',
    source: { name: 'Ayodhya Dham' },
    category: 'Temple',
  },
  {
    title: 'Deepotsav 2024: Over 25 lakh diyas lit on banks of Saryu river',
    description: 'Ayodhya broke its own Guinness World Record for the most earthen diyas lit simultaneously, celebrating the return of Lord Ram to his birthplace.',
    url: 'https://ayodhyadham.info',
    publishedAt: '2024-11-01T08:00:00Z',
    source: { name: 'Ayodhya Dham' },
    category: 'Festival',
  },
  {
    title: 'Infrastructure development: New highways and airports improve Ayodhya connectivity',
    description: 'Maharishi Valmiki International Airport now operates daily flights from Delhi, Mumbai, and other major cities, making Ayodhya more accessible to pilgrims.',
    url: 'https://ayodhyadham.info',
    publishedAt: '2024-12-15T08:00:00Z',
    source: { name: 'Ayodhya Dham' },
    category: 'Development',
  },
  {
    title: 'Ram Navami 2025: Special darshan arrangements announced for pilgrims',
    description: 'The Ram Janmabhoomi Trust has announced extended darshan timings and special queue management for the Ram Navami celebrations in April 2025.',
    url: 'https://ayodhyadham.info',
    publishedAt: '2025-03-01T08:00:00Z',
    source: { name: 'Ayodhya Dham' },
    category: 'Events',
  },
  {
    title: 'Hanuman Garhi renovation project: New facilities for pilgrims',
    description: 'The 76-step stairway and surrounding areas at Hanuman Garhi have been upgraded with better lighting, railings, and rest areas for elderly pilgrims.',
    url: 'https://ayodhyadham.info',
    publishedAt: '2025-01-10T08:00:00Z',
    source: { name: 'Ayodhya Dham' },
    category: 'Temple',
  },
  {
    title: 'Tourism numbers soar: Ayodhya sees 5 crore visitors post Ram Mandir',
    description: 'Ayodhya has seen unprecedented growth in tourism since the Ram Mandir consecration, with hotels, dharamshalas, and ghats reporting record occupancy.',
    url: 'https://ayodhyadham.info',
    publishedAt: '2025-02-05T08:00:00Z',
    source: { name: 'Ayodhya Dham' },
    category: 'Development',
  },
];

const CATEGORIES = ['All', 'Temple', 'Festival', 'Events', 'Development'];

const LOCAL_IMAGES = [
  '/images/design/hero-ram-mandir.webp',
  '/images/ghats/naya-ghat-2.jpg',
  '/images/hanuman/temple-1.webp',
  '/images/kanak-bhawan/temple.jpg',
  '/images/ghats/saryu-ghat-1.jpeg',
  '/images/design/ram-mandir-night.webp',
];

function fmtDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function News() {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchNews(i18n.language)
      .then((data) => {
        if (!alive) return;
        if (data && data.length > 0) { setArticles(data); setUsingFallback(false); }
        else { setArticles(FALLBACK_NEWS); setUsingFallback(true); }
      })
      .catch(() => { if (alive) { setArticles(FALLBACK_NEWS); setUsingFallback(true); } })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [i18n.language]);

  const filtered = articles.filter((a) => {
    const matchCat = category === 'All' || (a.category || 'Temple') === category;
    const q = query.toLowerCase();
    const matchQ = !q || a.title?.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q);
    return matchCat && matchQ;
  });

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Blog & Articles — Ayodhya',
    description: 'Stories, guides and updates about Ayodhya, Ram Mandir and the holy city.',
    url: 'https://ayodhyadham.info/blog',
  };

  return (
    <>
      <SEO
        title="Blog & Articles"
        description="Read stories, travel guides and the latest updates about Ayodhya, Ram Mandir, festivals and developments in the holy city."
        canonicalUrl="/blog"
        keywords="Ayodhya blog, Ayodhya news, Ram Mandir updates, Ayodhya travel articles"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Blog') }]} title={t('Blog & Articles')} subtitle={t('Read stories, guides and updates about Ayodhya')} />

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          <Tabs variant="pill" items={CATEGORIES.map((c) => ({ id: c, label: t(c) }))} value={category} onChange={setCategory} ariaLabel={t('Article category')} />
          <div className="listing__tools">
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search articles')}</span>
              <input type="search" placeholder={t('Search articles…')} value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
          </div>
        </div>

        {usingFallback && !loading && (
          <div className="notice notice--info" style={{ marginBottom: 20 }}>
            <Icon name="info" size={18} />
            <span>{t('Showing curated articles. Live news appears automatically once the news service is configured.')}</span>
          </div>
        )}

        {loading ? (
          <div className="card-grid">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={330} radius="var(--r-lg)" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="newspaper" title={t('No articles found')} body={query ? t('Nothing matches "{{query}}". Try another search.', { query }) : t('Check back soon for new stories.')} action={<Button variant="outline" onClick={() => { setQuery(''); setCategory('All'); }}>{t('View All Articles')}</Button>} />
        ) : (
          <>
            <div className="card-grid">
              {filtered.map((a, i) => (
                <ArticleCard
                  key={`${a.url}-${i}`}
                  image={a.image || LOCAL_IMAGES[i % LOCAL_IMAGES.length]}
                  category={a.category || a.source?.name || t('News')}
                  title={a.title}
                  excerpt={a.description}
                  date={fmtDate(a.publishedAt)}
                  href={a.url}
                />
              ))}
            </div>
            {(category !== 'All' || query) && (
              <div className="text-center" style={{ marginTop: 28 }}>
                <Button variant="outline" onClick={() => { setQuery(''); setCategory('All'); }}>{t('View All Articles')}</Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
